
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent } from '@/components/ui/card';
import { Users, Calendar } from 'lucide-react';
import { getInitials } from '@/utils/stringUtils';

interface GroupSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const GroupSelectionDialog: React.FC<GroupSelectionDialogProps> = ({
  open,
  onOpenChange
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Fetch user's groups
  const { data: userGroups, isLoading } = useQuery({
    queryKey: ['user-groups', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('group_members')
        .select(`
          group_id,
          groups:group_id (
            id,
            name,
            description,
            avatar_url
          )
        `)
        .eq('user_id', user.id);

      if (error) throw error;

      // Get member counts separately
      const groupsWithCounts = await Promise.all(
        data
          .filter(item => item.groups)
          .map(async (item) => {
            const { count } = await supabase
              .from('group_members')
              .select('*', { count: 'exact', head: true })
              .eq('group_id', item.groups.id);
            
            return {
              ...item.groups,
              member_count: count || 0
            };
          })
      );

      return groupsWithCounts;
    },
    enabled: !!user && open
  });

  const handleGroupSelect = (groupId: string) => {
    setLoading(true);
    // Pass navigation context for proper back navigation
    const fromPage = window.location.pathname;
    const fromTitle = fromPage === '/' ? 'Home' : 'Previous Page';
    navigate(`/groups/${groupId}?tab=polls&createPoll=true&fromPage=${encodeURIComponent(fromPage)}&fromTitle=${encodeURIComponent(fromTitle)}`);
    onOpenChange(false);
    setLoading(false);
  };

  if (isLoading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Select Group for Poll
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Select Group for Poll
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {userGroups && userGroups.length > 0 ? (
            userGroups.map((group: any) => (
              <Card 
                key={group.id} 
                className="cursor-pointer hover:bg-accent transition-colors"
                onClick={() => handleGroupSelect(group.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center space-x-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={group.avatar_url || ''} />
                      <AvatarFallback className="bg-primary/10">
                        {getInitials(group.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium truncate">{group.name}</h3>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {group.member_count} member{group.member_count !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-medium mb-2">No Groups Found</h3>
              <p className="text-sm text-muted-foreground mb-4">
                You need to be a member of a group to create polls.
              </p>
              <Button onClick={() => navigate('/groups')}>
                Explore Groups
              </Button>
            </div>
          )}
        </div>

        {userGroups && userGroups.length > 0 && (
          <div className="flex justify-end pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
